import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { useAccCompanies } from '../companies/useData';
import { useAccPeriods } from '../periods/useData';
import { useAccAccountTree } from '../accounts/useData';
import {
  collectExpandableNodeIds,
  flattenAccountTree,
  type TreeListRow,
} from '../accounts/treeListUtils';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import {
  Button,
  Card,
  ChevronDownIcon,
  ChevronRightIcon,
  CollapseAllIcon,
  DocumentTextIcon,
  ExpandAllIcon,
  FolderIcon,
  PencilIcon,
  SearchIcon,
  Switch,
  TextInput,
  SegmentedControl,
} from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { moduleConfig as accAccountsConfig } from '../accounts/config';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { PeriodDateRangeFields } from './shared/reportFields';
import { accountingReturnState } from '../shared/navigation';
import {
  buildAccountParentMap,
  loadAnalyticalLedgersState,
  persistAnalyticalLedgersState,
  uncollapsePathToAccount,
  type AnalyticalLedgersSearchMode,
} from './analyticalLedgersState';
import { pickDefaultAccountingPeriod } from './shared/reportUtils';

type SearchMode = AnalyticalLedgersSearchMode;

type LedgerRow = {
  id: number;
  parentId: number | null;
  code: string;
  combLevel: string;
  name: string;
  isPosting: boolean;
  level1: string;
  level2: string;
  level3: string;
  level4: string;
  depth: number;
  broughtForwardUSD: number | null;
  periodBalanceUSD: number | null;
  totalBalanceUSD: number | null;
};

type AnalyticalLedgersResponse = {
  dateFrom: string;
  dateTo: string;
  companyIds: number[];
  rows: LedgerRow[];
};

const formatAmount = (value: number) =>
  value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatGridBalance = (row: TreeListRow, value: number | null | undefined) => {
  if (!row.isPosting) return '';
  if (value == null) return '—';
  if (value === 0) return '';
  return formatAmount(value);
};

const matchesSearch = (row: LedgerRow, term: string, mode: SearchMode) => {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  if (mode === 'levelCode') {
    return row.code.toLowerCase().includes(q) || row.combLevel.toLowerCase().includes(q);
  }
  if (mode === 'narrative') {
    return row.name.toLowerCase().includes(q);
  }
  return [row.level1, row.level2, row.level3, row.level4, row.name]
    .some((value) => value.toLowerCase().includes(q));
};


const toolbarBtnClass = (colors: ReturnType<typeof useTheme>['colors']) =>
  cn(
    'inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
    colors.border,
    colors.surface,
    colors.text,
    colors.interactiveHover,
    'disabled:pointer-events-none disabled:opacity-50',
  );

export const AnalyticalLedgers: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { can } = useAuth();
  const scrollPendingAccountRef = useRef<number | null>(null);
  const defaultPeriodAppliedRef = useRef(false);

  const canEditLedger = can(accAccountsConfig.module.permissionModuleKey ?? 'acc_accounts', 'edit');

  const { data: companiesRaw } = useAccCompanies('', 1, 500);
  const companies = useMemo(
    () => [...(companiesRaw ?? [])].sort((a, b) => a.code.localeCompare(b.code)),
    [companiesRaw],
  );

  const { data: periodsRaw } = useAccPeriods('', 1, 100);
  const periods = periodsRaw ?? [];

  const { data: accountTree } = useAccAccountTree({ lite: true });

  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [allCompanies, setAllCompanies] = useState(false);
  const [companyFilter, setCompanyFilter] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchMode, setSearchMode] = useState<SearchMode>('levelName');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());
  const [runToken, setRunToken] = useState(0);
  const [autoLoaded, setAutoLoaded] = useState(false);
  const [stateHydrated, setStateHydrated] = useState(false);

  const buildPersistedState = useCallback((
    accountId: number | null = selectedAccountId,
  ) => ({
    dateFrom,
    dateTo,
    periodId,
    allCompanies,
    selectedCompanyIds,
    companyFilter,
    searchMode,
    searchTerm,
    collapsedIds: Array.from(collapsedIds),
    selectedAccountId: accountId,
  }), [
    allCompanies,
    collapsedIds,
    companyFilter,
    dateFrom,
    dateTo,
    periodId,
    searchMode,
    searchTerm,
    selectedAccountId,
    selectedCompanyIds,
  ]);

  useEffect(() => {
    if (stateHydrated) return;

    const stored = loadAnalyticalLedgersState();
    const urlDateFrom = searchParams.get('dateFrom') ?? '';
    const urlDateTo = searchParams.get('dateTo') ?? '';
    const urlCompanyIds = searchParams.get('companyIds') ?? '';
    const urlSelectedRaw = searchParams.get('selectedAccountId');
    const urlSelectedId = urlSelectedRaw ? Number(urlSelectedRaw) : null;

    const dateFromVal = urlDateFrom || stored?.dateFrom || '';
    const dateToVal = urlDateTo || stored?.dateTo || '';

    if (stored) {
      setAllCompanies(false);
      setSelectedCompanyIds(stored.selectedCompanyIds);
      setCompanyFilter(stored.companyFilter);
      setSearchMode(stored.searchMode);
      setSearchTerm(stored.searchTerm);
      setCollapsedIds(new Set(stored.collapsedIds));
    }

    if (urlCompanyIds) {
      const ids = urlCompanyIds
        .split(',')
        .map((id) => Number(id.trim()))
        .filter((id) => Number.isFinite(id));
      setAllCompanies(false);
      setSelectedCompanyIds(ids);
    }

    if (dateFromVal) setDateFrom(dateFromVal);
    if (dateToVal) setDateTo(dateToVal);

    if (stored?.periodId) {
      setPeriodId(stored.periodId);
    } else if (dateFromVal && dateToVal && periods.length > 0) {
      const match = periods.find(
        (p) => p.startDate.slice(0, 10) === dateFromVal && p.endDate.slice(0, 10) === dateToVal,
      );
      setPeriodId(match ? String(match.id) : '');
    }

    const selectedId =
      urlSelectedId != null && Number.isFinite(urlSelectedId)
        ? urlSelectedId
        : stored?.selectedAccountId ?? null;
    if (selectedId != null) {
      setSelectedAccountId(selectedId);
      scrollPendingAccountRef.current = selectedId;
    }

    const restoredCompanyIds = urlCompanyIds
      ? urlCompanyIds
          .split(',')
          .map((id) => Number(id.trim()))
          .filter((id) => Number.isFinite(id))
      : stored?.selectedCompanyIds ?? [];
    if (dateFromVal && dateToVal && restoredCompanyIds.length > 0) {
      setRunToken(1);
      setAutoLoaded(true);
      defaultPeriodAppliedRef.current = true;
    }

    setStateHydrated(true);
  }, [periods, searchParams, stateHydrated]);

  useEffect(() => {
    if (!periodId) return;
    const period = periods.find((p) => String(p.id) === periodId);
    if (!period) return;
    setDateFrom(period.startDate.slice(0, 10));
    setDateTo(period.endDate.slice(0, 10));
  }, [periodId, periods]);

  useEffect(() => {
    if (!stateHydrated || periods.length === 0) return;
    if (defaultPeriodAppliedRef.current) return;
    if (dateFrom && dateTo) {
      defaultPeriodAppliedRef.current = true;
      return;
    }
    const match = pickDefaultAccountingPeriod(periods);
    if (match) {
      setPeriodId(String(match.id));
      setDateFrom(match.startDate.slice(0, 10));
      setDateTo(match.endDate.slice(0, 10));
      defaultPeriodAppliedRef.current = true;
    }
  }, [stateHydrated, periods, dateFrom, dateTo]);

  useEffect(() => {
    if (
      !autoLoaded &&
      stateHydrated &&
      dateFrom &&
      dateTo &&
      (allCompanies || selectedCompanyIds.length > 0)
    ) {
      setRunToken(1);
      setAutoLoaded(true);
    }
  }, [autoLoaded, stateHydrated, dateFrom, dateTo, allCompanies, selectedCompanyIds]);

  const companyIdsParam = allCompanies ? '' : selectedCompanyIds.join(',');
  const hasCompanyScope = allCompanies || selectedCompanyIds.length > 0;

  const reportUrl =
    runToken > 0 && dateFrom && dateTo && hasCompanyScope
      ? `/accounting/reports/analytical-ledgers?dateFrom=${dateFrom}&dateTo=${dateTo}${companyIdsParam ? `&companyIds=${companyIdsParam}` : ''}&_run=${runToken}`
      : null;

  const { data: report, isLoading } = useSWR<AnalyticalLedgersResponse>(reportUrl, fetcher);

  const levelByAccountId = useMemo(() => {
    const map = new Map<number, LedgerRow>();
    for (const row of report?.rows ?? []) {
      map.set(row.id, row);
    }
    return map;
  }, [report?.rows]);

  const expandableIds = useMemo(() => collectExpandableNodeIds(accountTree), [accountTree]);
  const isSearchActive = searchTerm.trim().length > 0;

  useEffect(() => {
    if (selectedAccountId == null || accountTree.length === 0) return;
    const parentMap = buildAccountParentMap(accountTree);
    setCollapsedIds((prev) => uncollapsePathToAccount(prev, selectedAccountId, parentMap));
  }, [selectedAccountId, accountTree]);

  const gridRows = useMemo((): TreeListRow[] => {
    const collapsed = isSearchActive ? new Set<number>() : collapsedIds;
    const flat = flattenAccountTree(accountTree, collapsed);
    if (!isSearchActive) return flat;
    return flat.filter((row) => {
      const levels = levelByAccountId.get(row.id);
      const fallback: LedgerRow = {
        id: row.id,
        parentId: row.parentId ?? null,
        code: row.code,
        combLevel: row.combLevel ?? row.code,
        name: row.name,
        isPosting: row.isPosting,
        level1: '',
        level2: '',
        level3: '',
        level4: '',
        depth: row.depth,
        broughtForwardUSD: null,
        periodBalanceUSD: null,
        totalBalanceUSD: null,
      };
      return matchesSearch(levels ?? fallback, searchTerm, searchMode);
    });
  }, [accountTree, collapsedIds, searchTerm, searchMode, levelByAccountId, isSearchActive]);

  useEffect(() => {
    const pendingId = scrollPendingAccountRef.current;
    if (pendingId == null || isLoading || runToken === 0) return;
    const timer = window.setTimeout(() => {
      const row = document.querySelector(`[data-row-id="${pendingId}"]`);
      row?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      scrollPendingAccountRef.current = null;
    }, 150);
    return () => window.clearTimeout(timer);
  }, [isLoading, runToken, gridRows.length, selectedAccountId]);

  const filteredCompanies = useMemo(() => {
    const q = companyFilter.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q),
    );
  }, [companies, companyFilter]);

  const toggleCompany = (id: number) => {
    setAllCompanies(false);
    setSelectedCompanyIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
  };

  const toggleAllCompanies = (checked: boolean) => {
    setAllCompanies(checked);
    setSelectedCompanyIds([]);
  };

  const toggleCollapsed = useCallback((id: number) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = () => setCollapsedIds(new Set());
  const collapseAll = () => setCollapsedIds(new Set(expandableIds));

  const syncListUrl = useCallback((accountId: number | null = selectedAccountId) => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (!allCompanies && selectedCompanyIds.length > 0) {
      params.set('companyIds', selectedCompanyIds.join(','));
    }
    if (accountId != null) params.set('selectedAccountId', String(accountId));
    const qs = params.toString();
    navigate(`/accounting/reports/analytical-ledgers${qs ? `?${qs}` : ''}`, { replace: true });
  }, [allCompanies, dateFrom, dateTo, navigate, selectedAccountId, selectedCompanyIds]);

  const runReport = () => {
    persistAnalyticalLedgersState(buildPersistedState());
    setRunToken((value) => value + 1);
    syncListUrl();
  };

  const openLedgerKartela = useCallback((accountId: number) => {
    persistAnalyticalLedgersState(buildPersistedState(accountId));
    const params = new URLSearchParams({ dateFrom, dateTo });
    if (companyIdsParam) params.set('companyIds', companyIdsParam);
    navigate(`/accounting/reports/ledger-kartela/${accountId}?${params.toString()}`, {
      state: accountingReturnState(location.pathname, location.search),
    });
  }, [buildPersistedState, companyIdsParam, dateFrom, dateTo, location.pathname, location.search, navigate]);

  const persistBeforeLedgerEdit = useCallback((accountId: number) => {
    setSelectedAccountId(accountId);
    persistAnalyticalLedgersState(buildPersistedState(accountId));
  }, [buildPersistedState]);

  const renderRowActions = useCallback((row: TreeListRow) => {
    if (!row.isPosting) return null;
    return (
      <div className="flex items-center flex-nowrap justify-end">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedAccountId(row.id);
            openLedgerKartela(row.id);
          }}
          className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
          title="Ledger card"
          aria-label="Ledger card"
        >
          <DocumentTextIcon className="w-4 h-4" />
        </button>
        {canEditLedger && (
          <Link
            to={accAccountsConfig.module.route.edit(row.id)}
            state={accountingReturnState(location.pathname, location.search)}
            className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
            onClick={(e) => {
              e.stopPropagation();
              persistBeforeLedgerEdit(row.id);
            }}
            title="Edit ledger"
            aria-label="Edit ledger"
          >
            <PencilIcon className="w-4 h-4" />
          </Link>
        )}
      </div>
    );
  }, [canEditLedger, colors.interactiveHover, colors.primaryFg, location.pathname, location.search, openLedgerKartela, persistBeforeLedgerEdit]);

  const periodOptions = useMemo(
    () => [
      { value: '', label: 'Custom period' },
      ...periods.map((p) => ({
        value: String(p.id),
        label: `${p.year} (${formatDateGlobal(p.startDate)} – ${formatDateGlobal(p.endDate)})`,
      })),
    ],
    [periods],
  );

  const columns = useMemo((): Column<TreeListRow>[] => [
    {
      key: '_tree',
      header: '',
      width: '2.75rem',
      render: (_v, row) => (
        <div className="flex items-center min-w-0" style={{ paddingLeft: row.depth * 18 }}>
          {row.hasChildren ? (
            <button
              type="button"
              className={cn('shrink-0 rounded-md p-1', colors.textMuted, colors.interactiveHover)}
              onClick={(e) => {
                e.stopPropagation();
                toggleCollapsed(row.id);
              }}
              aria-label={collapsedIds.has(row.id) ? 'Expand' : 'Collapse'}
            >
              {collapsedIds.has(row.id) ? <ChevronRightIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
            </button>
          ) : (
            <span className="w-6 shrink-0" aria-hidden />
          )}
        </div>
      ),
    },
    {
      key: 'combLevel',
      header: 'Comb level',
      width: '11rem',
      render: (_v, row) => {
        const levels = levelByAccountId.get(row.id);
        const code = levels?.combLevel ?? row.combLevel ?? row.code;
        return (
          <span className={cn('inline-flex min-w-0 items-center gap-1.5', colors.text)}>
            {row.isPosting ? (
              <span className={cn('shrink-0', isDark ? 'text-sky-400' : 'text-sky-600')} title="Ledger">
                <DocumentTextIcon className="h-3.5 w-3.5" aria-hidden />
              </span>
            ) : (
              <span className={cn('shrink-0', isDark ? 'text-violet-400' : 'text-violet-500')} title="Header">
                <FolderIcon className="h-3.5 w-3.5" aria-hidden />
              </span>
            )}
            <span className="truncate tabular-nums" title={code}>{code}</span>
          </span>
        );
      },
    },
    {
      key: 'level4',
      header: 'Level 4',
      width: '17.5rem',
      maxWidth: '28.5rem',
      render: (_v, row) => {
        const levels = levelByAccountId.get(row.id);
        const label = levels?.level4 || levels?.name || row.name;
        return (
          <span className="truncate block" title={label}>{label}</span>
        );
      },
    },
    {
      key: '_spacer',
      header: '',
      render: () => null,
    },
    {
      key: 'broughtForwardUSD',
      header: 'Brought forward (USD)',
      align: 'right',
      width: '9rem',
      render: (_v, row) => (
        <span className="tabular-nums">
          {formatGridBalance(row, levelByAccountId.get(row.id)?.broughtForwardUSD)}
        </span>
      ),
    },
    {
      key: 'periodBalanceUSD',
      header: 'Period balance (USD)',
      align: 'right',
      width: '9rem',
      render: (_v, row) => (
        <span className="tabular-nums">
          {formatGridBalance(row, levelByAccountId.get(row.id)?.periodBalanceUSD)}
        </span>
      ),
    },
    {
      key: 'totalBalanceUSD',
      header: 'Total balance (USD)',
      align: 'right',
      width: '9rem',
      render: (_v, row) => (
        <span className="tabular-nums">
          {formatGridBalance(row, levelByAccountId.get(row.id)?.totalBalanceUSD)}
        </span>
      ),
    },
    {
      key: '_actions',
      header: '',
      width: '4.5rem',
      align: 'right',
      render: (_v, row) => renderRowActions(row),
    },
  ], [
    collapsedIds,
    colors.interactiveHover,
    colors.text,
    colors.textMuted,
    isDark,
    levelByAccountId,
    renderRowActions,
    toggleCollapsed,
  ]);

  const hasRun = runToken > 0;
  const canLoad = Boolean(dateFrom && dateTo && (allCompanies || selectedCompanyIds.length > 0));

  return (
    <div className="flex flex-col h-full min-h-0 p-1 gap-1">
      <Card className="p-4 shrink-0">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <PeriodDateRangeFields
            periodId={periodId}
            periodOptions={periodOptions}
            onPeriodChange={setPeriodId}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={(value) => {
              setPeriodId('');
              setDateFrom(value);
            }}
            onDateToChange={(value) => {
              setPeriodId('');
              setDateTo(value);
            }}
            periodClassName="w-[15rem] shrink-0"
          />

          <div className="flex flex-wrap items-center gap-3 flex-1 min-w-0 pb-0.5">
            <SegmentedControl
              size="sm"
              aria-label="Search mode"
              value={searchMode}
              onChange={(value) => setSearchMode(value as SearchMode)}
              options={[
                { id: 'levelName', label: 'Level name' },
                { id: 'levelCode', label: 'Level code' },
                { id: 'narrative', label: 'Narrative' },
              ]}
            />

            <div className="flex-1 min-w-[12rem] max-w-md">
              <TextInput
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Filter chart of accounts…"
                prefix={<SearchIcon className="h-4 w-4" />}
                showClearButton={Boolean(searchTerm)}
              />
            </div>

            <Button
              variant="default"
              onClick={runReport}
              disabled={!canLoad}
              className="h-9 px-4 gap-2 ml-auto shrink-0"
            >
              <SearchIcon className="h-4 w-4" />
              Search
            </Button>
          </div>
        </div>
      </Card>

      <div
        className={cn(
          'flex flex-1 min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <aside className={cn('w-64 shrink-0 border-r flex flex-col min-h-0', colors.border, colors.surfaceHover)}>
          <div className={cn('px-3 pt-3 pb-2 space-y-2.5 border-b', colors.border, colors.surface)}>
            <div className="flex items-center justify-between gap-2">
              <h2 className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>
                Companies
              </h2>
              <span className={cn('text-[11px] tabular-nums', colors.textMuted)}>
                {allCompanies
                  ? `${companies.length} all`
                  : `${selectedCompanyIds.length}/${companies.length}`}
              </span>
            </div>

            <div
              className={cn(
                'flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5',
                colors.border,
                allCompanies ? cn(colors.info, 'ring-1', colors.primaryBorderAll) : colors.surface,
              )}
            >
              <Switch
                label="All companies"
                checked={allCompanies}
                onChange={(e) => toggleAllCompanies(e.target.checked)}
                size="sm"
              />
            </div>

            <TextInput
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              placeholder="Filter by code or name…"
              prefix={<SearchIcon className="h-4 w-4" />}
              showClearButton={Boolean(companyFilter)}
              size="sm"
            />
          </div>

          <div className={cn('dashboard-scrollbar flex-1 overflow-y-auto py-1', colors.surface)}>
            {filteredCompanies.map((company) => {
              const active = !allCompanies && selectedCompanyIds.includes(company.id);
              const checked = allCompanies || active;
              const label = `${company.name}`;
              return (
                <label
                  key={company.id}
                  title={label}
                  className={cn(
                    'group mx-1.5 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 transition-colors',
                    active && cn(colors.info, 'ring-1', colors.primaryBorderAll),
                    !active && checked && colors.surfaceHover,
                    !checked && colors.interactiveHover,
                  )}
                >
                  <input
                    type="checkbox"
                    className={cn(
                      'h-3.5 w-3.5 shrink-0 rounded focus:ring-indigo-500',
                      isDark
                        ? 'border-white/20 bg-[#121a2b] text-[#6b8fb8]'
                        : 'border-slate-300 text-indigo-600',
                    )}
                    checked={checked}
                    onChange={() => toggleCompany(company.id)}
                    aria-label={label}
                  />
                  <span
                    className={cn(
                      'min-w-[2.25rem] shrink-0 text-sm tabular-nums',
                      active ? colors.primaryFg : colors.text,
                    )}
                  >
                    {company.code}
                  </span>
                  <span className={cn('min-w-0 flex-1 truncate text-sm', colors.text)}>
                    {company.name}
                  </span>
                </label>
              );
            })}
            {filteredCompanies.length === 0 && (
              <p className={cn('px-3 py-8 text-sm text-center', colors.textMuted)}>No companies match.</p>
            )}
          </div>
        </aside>

        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div className={cn('flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b', colors.border, colors.surfaceHover)}>
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              {hasRun && dateFrom && dateTo && (
                <Badge variant="info" className="font-normal">
                  {formatDateGlobal(dateFrom)} – {formatDateGlobal(dateTo)}
                </Badge>
              )}
              <span className={cn('text-sm', colors.textMuted)}>
                {hasRun
                  ? `${gridRows.length} account${gridRows.length === 1 ? '' : 's'}`
                  : 'Choose period and companies, then search'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={expandAll}
                disabled={!hasRun || isSearchActive || expandableIds.length === 0}
                className={toolbarBtnClass(colors)}
                title="Expand all"
                aria-label="Expand all"
              >
                <ExpandAllIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={collapseAll}
                disabled={!hasRun || isSearchActive || expandableIds.length === 0}
                className={toolbarBtnClass(colors)}
                title="Collapse all"
                aria-label="Collapse all"
              >
                <CollapseAllIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 flex flex-col">
            {isLoading && (
              <div className={cn('flex items-center justify-center flex-1 text-sm', colors.textMuted)}>
                Loading analytical ledgers…
              </div>
            )}
            {!isLoading && !hasRun && (
              <div className={cn('flex flex-col items-center justify-center flex-1 min-h-[200px] px-8 text-center', colors.textMuted)}>
                <div className={cn('h-12 w-12 rounded-full flex items-center justify-center mb-3', colors.surfaceHover)}>
                  <SearchIcon className="h-5 w-5 opacity-60" />
                </div>
                <p className="text-sm font-medium">Ready to search</p>
                <p className="text-xs mt-1 max-w-sm leading-relaxed">
                  Pick a period and company scope, then click Search to browse saved journal balances by account.
                </p>
              </div>
            )}
            {!isLoading && hasRun && (
              <DataTable
                {...ACCOUNTING_TABLE_DEFAULTS}
                data={gridRows}
                columns={columns}
                keyExtractor={(row) => row.id}
                selectedRowId={selectedAccountId ?? undefined}
                emptyMessage="No accounts match your filter."
                stickyHeader
                tableWrapperClassName="w-full block min-w-full"
                containerClassName="h-full min-h-0 border-0 shadow-none rounded-none"
                tableClassName={cn('w-full table-fixed divide-y', colors.border)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
